import { ChangeDetectorRef, Component, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import {DatePipe, formatDate} from "@angular/common";
import {animate, state, style, transition, trigger} from "@angular/animations";
import {ReportService} from "../../../services/report.service";
import { Subscription } from 'rxjs';
import {DeviceService} from "../../../services/device.service";
import {AuthService} from "../../../services/auth.service";
import {ReportTagsComponent} from "../report-tags/report-tags.component";
import {SharedService} from "../../../services/shared.service";
import {Company} from 'src/app/interfaces/company.interface';

export const smoothScrollAnimation = [
  trigger('fadeInOut', [
    state('in', style({
      opacity: 1,
      transform: 'translateY(0)',
    })),

    // Enter animation
    transition(':enter', [
      style({
        opacity: 0,
        transform: 'translateY(-15px)' // Reduced distance for subtlety
      }),
      animate('600ms cubic-bezier(0.4, 0, 0.2, 1)', style({
        opacity: 1,
        transform: 'translateY(0)'
      }))
    ]),

    // Leave animation
    transition(':leave', [
      animate('400ms cubic-bezier(0.4, 0, 0.2, 1)', style({
        opacity: 0,
        transform: 'translateY(-15px)'
      }))
    ])
  ])
];

export const defaultScrollAnimation = [
  trigger('fadeInOut', [
    state('in', style({ opacity: 1, transform: 'translateY(0)' })),
    transition(':enter', [
      style({ opacity: 0, transform: 'translateY(-20px)' }), // Start 20px above
      animate('500ms ease-out')
    ]),
    transition(':leave', [
      animate('0ms ease-out', style({ opacity: 0, transform: 'translateY(-20px)' })) // Exit to 20px above
    ])
  ])
]

@Component({
  selector: 'app-profile-header',
  templateUrl: './profile-header.component.html',
  styleUrls: ['./profile-header.component.scss'],
  animations: [smoothScrollAnimation]
})
export class ProfileHeaderComponent implements OnInit, OnChanges, OnChanges {

  @Input() reportSearch: any;
  @Input() company: Company;

  icpScore: number = null;

  report: any = {
    person_avatar: null,
    person_full_name: '',
    person_city: '',
    person_state: '',
    person_country: '',
    person_job_title: '',
    linkedin_url: '',
    twitter_url: '',
    facebook_url: '',
    tagline: ''
  }

  isLoading: boolean = false;
  state = 'in'; // Control animation state
  user: any;
  isMobile: boolean = false;

  /** Emitted when the user presses "Regenerate"; the host report owns the modal. */
  @Output() regenerateClicked = new EventEmitter<void>();

  constructor(
    private reportService: ReportService,
    private deviceService: DeviceService,
    private sharedService: SharedService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef
  ) {

    this.reportSubscription = reportService.report$.subscribe(
      report => {
        this.reportSearch = report;
      });

    this.authService.authUser().subscribe(user => {
      this.user = user;
    });

  }

  @HostListener('window:scroll', ['$event'])

  onScroll(event: any) {
    const threshold = 100; // Set scroll threshold as needed
    const currentScroll = window.scrollY || document.documentElement.scrollTop;
    this.state = currentScroll > threshold ? 'out' : 'in';
  }

  @Output() dataEmitter: EventEmitter<any> = new EventEmitter();

  @Output() shareClicked: EventEmitter<void> = new EventEmitter();
  @Output() tagsClicked: EventEmitter<void> = new EventEmitter();
  @Output() icpScoreClicked: EventEmitter<void> = new EventEmitter();
  @Output() editPromptToggled = new EventEmitter<any>();
  @Output() reportLoaderClicked: EventEmitter<void> = new EventEmitter();
  @Output() companyDetailsModalRequested = new EventEmitter<any>();
  @Output() editRevenueModalRequested = new EventEmitter<{short: string, long: string}>();

  reportSubscription: Subscription;
  // report: any;

  tabs = [
    { id: 1, name: 'Person Insights', current: true },
    { id: 2, name: 'Company Insights', current: false },
    // { id: 3, name: 'Industry Insights', current: false },
  ]

  personExpanded = true
  companyExpanded = true

  tagsOpen: boolean = false;
  selectedFilters: { category: string, value: string, label: string }[] = [];

  // Get a reference to the ReportTagsComponent
  @ViewChild(ReportTagsComponent) tagComponent: ReportTagsComponent;

  @Output() tagsHeightChange = new EventEmitter<number>();

  // Receive the height from app-report-tags and emit it to the parent
  updateTagsHeight(height: number) {
    // console.log('[profile-header] updateTagsHeight')
    this.tagsHeightChange.emit(height);
  }

  ngOnInit(): void {

    // nullish coalescing operator (??)
    // optional chaining (?.)

    this.selectedFilters = []; // Initialize with an empty array

    // Initialize personExpanded and companyExpanded based on device type
    if (!this.deviceService.getIsDesktop()) {
      this.personExpanded = false
      this.companyExpanded = false
    }

    // Subscribe to report observable
    this.reportSubscription = this.reportService.report$.subscribe(
      report => {

        this.reportSearch = report; // Update the report

        if (this.reportSearch && this.reportSearch.tagsData) {
          this.populateSelectedFiltersFromReport(this.reportSearch.tagsData);
        }

        this.report = {
          person_avatar: this.reportSearch.search_person?.profile_picture ?? null,
          person_full_name: this.reportSearch.uniqueProfile?.person_full_name ?? this.reportSearch.name ?? 'N/A',
          person_city: this.reportSearch.uniqueProfile?.person_city ?? this.reportSearch.search_person?.enhanced_data?.city ?? '',
          person_state: this.reportSearch.uniqueProfile?.person_state ?? this.reportSearch.search_person?.enhanced_data?.state ?? '',
          person_country: this.reportSearch.uniqueProfile?.person_country ?? this.reportSearch.country ?? '',
          person_job_title: this.reportSearch.uniqueProfile?.person_job_title ?? this.reportSearch.search_person?.enhanced_data?.title ?? '',
          linkedin_url: this.reportSearch.search_person.enhanced_data?.linkedin_url ?? '',
          twitter_url: this.reportSearch.search_person.enhanced_data?.twitter_url ?? '',
          facebook_url: this.reportSearch.search_person.enhanced_data?.facebook_url ?? '',
          tagline: this.reportSearch.search_person?.tagline != '[]',
          company_logo: this.reportSearch.uniqueProfile?.company_logo ?? this.reportSearch?.search_person?.enhanced_data?.organization?.logo_url ?? null, // (this.reportSearch?.search_person?.enhanced_data?.organization?.logo_url == null || this.reportSearch?.search_person?.enhanced_data?.organization?.logo_url ==  '[]' ) ? 'https://ui-avatars.com/api/?background=FFA500&color=fff&rounded=true&name='+this.reportSearch.search_person.enhanced_data.organization.name+'&format=svg&size=128'  : this.reportSearch?.search_person?.enhanced_data?.organization?.logo_url,
          company_name: this.reportSearch.uniqueProfile?.company_name ?? this.reportSearch?.search_person?.enhanced_data?.organization?.name ?? 'N/A',
          company_city: this.reportSearch.uniqueProfile?.company_city ?? this.reportSearch.search_person?.enhanced_data?.organization?.city ? this.reportSearch.search_person?.enhanced_data?.organization?.city + "," :  '',
          company_state: this.reportSearch.uniqueProfile?.company_state ?? this.reportSearch.search_person?.enhanced_data?.organization?.state ? this.reportSearch.search_person?.enhanced_data?.organization?.state + "," : '',
          company_country: this.reportSearch.uniqueProfile?.company_country ?? this.reportSearch.search_person?.enhanced_data?.organization?.country ?? '',
          company_description: this.reportSearch.uniqueProfile?.company_description ?? this.reportSearch.search_person?.enhanced_data?.organization?.short_description ?? ''
        }

        /* commented on: 20.11.2025
        if (this.reportSearch.uniqueProfile?.person_avatar) {
          // this.report.person_avatar = this.reportSearch.uniqueProfile?.person_avatar;
          this.report.person_avatar = this.reportSearch.search_person?.profile_picture;
        } else if (this.reportSearch.picture && this.reportSearch.picture !== '[]') {
          // this.report.person_avatar = this.reportSearch.picture;
          this.report.person_avatar = this.reportSearch.search_person?.profile_picture;
        } else {
          this.report.person_avatar = 'https://ui-avatars.com/api/?background=FFA500&color=fff&rounded=true&name='+this.reportSearch.name+'&format=svg&size=128';
        }
        */

        if (this.reportSearch.is_loading) {
          this.isLoading = this.reportSearch.is_loading
        }

      });

    // Populate selectedFilters from report.tagsData
    if (this.reportSearch && this.reportSearch.tagsData) {
      this.populateSelectedFiltersFromReport(this.reportSearch.tagsData);
    }

    this.detectMobile();

    // Listen for report finished event
    this.sharedService.eventEmitter.subscribe((event) => {
      const eventType = event.type;
      const eventData = event.data;

      if (eventType === 'REPORT_FINISHED' && Number(eventData.report_id) === Number(this.reportSearch.id)) {
        this.isLoading = false;
      }
    });

  }

  ngOnChanges(changes: SimpleChanges): void {

    if (changes['reportSearch'] && changes['reportSearch'].currentValue) {
      const tagsData = changes['reportSearch'].currentValue.tagsData;
      if (tagsData) {
        this.populateSelectedFiltersFromReport(tagsData);
      }
    }

    if (changes['company'] && changes['company'].currentValue) {
      // Update the ICP score when the company input changes
      this.icpScore = changes['company'].currentValue.icp_score;
      //console.log('ICP Score updated:', this.icpScore);
    }

  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.detectMobile();
  }

  detectMobile() {
    const width = window.innerWidth;

    // You can set the width threshold, e.g., 768px for tablets and smaller devices
    this.isMobile = width <= 768;
  }

  // Function to populate selectedFilters based on report.tagsData
  populateSelectedFiltersFromReport(tagsData: any[]): void {
    // console.log('populateSelectedFiltersFromReport')

    if (!tagsData || tagsData.length === 0) {
      return;
    }

    // Populate selectedFilters
    this.selectedFilters = tagsData.map(tag => ({
      category: this.toCamelCase(tag.type),
      value: tag.slug,
      label: tag.name
    }));

    // console.log('Populated selectedFilters:', this.selectedFilters); // Debugging output

    // Trigger change detection
    this.cdr.detectChanges();
  }
  onImageError(event: Event) {
    const imgElement = event.target as HTMLImageElement;
    // imgElement.src = 'assets/images/human.svg'; // Fallback image when error occurs
    imgElement.src = 'https://ui-avatars.com/api/?background=FFA500&color=fff&rounded=true&name='+this.report.name+'&format=svg&size=128';
  }
  toCamelCase(str: string): string {
    return str
      .replace(/[-_\s]+(.)?/g, (match, chr) => chr ? chr.toUpperCase() : '')
      .replace(/^[A-Z]/, match => match.toLowerCase());
  }

  formatDate(date) {
    return new DatePipe('en-US').transform(date, 'MMM d');
  }

  scrollToAnchor(tabIndex: number): void {

    this.changeTab(tabIndex)
    let anchorId = 'personInsights'
    if (tabIndex === 0) { // Person Insights
      anchorId = 'personInsights'
    } else if (tabIndex === 1) {
      anchorId = 'companyInsights'
    } else if (tabIndex === 2) {
      anchorId = 'industryInsights'
    }

    const element = document.getElementById(anchorId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }
  // Scroll for Mobile, where we have option > select, not button
  scrollTo(event: Event): void {
    const target = event.target as HTMLSelectElement;
    const sectionId = target.value;
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  changeTab(tabIndex: number) {
    // console.log('changeTab')

    this.tabs.forEach((tab) => {
      tab.current = false
    })

    this.tabs[tabIndex].current = true

    // Call updateHeight on tagComponent if it exists to recalculate the height
    if (this.tagComponent) {
      this.tagComponent.updateHeight();
    }
  }

  setActiveTab(currentSectionId: string | null) {
    if (currentSectionId === 'personInsights') {
      this.changeTab(0)
    } else if (currentSectionId === 'companyInsights') {
      this.changeTab(1)
    } else if (currentSectionId === 'industryInsights') {
      this.changeTab(2)
    }
  }

  expandPerson() {
    this.personExpanded = !this.personExpanded
  }

  expandCompany() {
    this.companyExpanded = !this.companyExpanded
  }

  setExpandPersonTrue() {
    this.personExpanded = true
  }

  expandPersonAndCompany(expanded: boolean) {
    this.personExpanded = expanded
    this.companyExpanded = expanded

    this.dataEmitter.emit(expanded);
  }

  onShare(): void {
    this.shareClicked.emit();
  }

  workspaceAcronym(name: string | null | undefined): string {
    if (!name) return 'N/A';
    const words = name.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) return 'N/A';
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
    return (words[0][0] + words[1][0]).toUpperCase();
  }

  onReportLoader(): void {
    this.reportLoaderClicked.emit();
  }

  slug(input) {

    // const romanianMap = {
    //   'Ă': 'A', 'Â': 'A', 'Î': 'I', 'Ș': 'S', 'Ț': 'T',
    //   'ă': 'a', 'â': 'a', 'î': 'i', 'ș': 's', 'ț': 't'
    // };

    return input
      .trim()
      .toLowerCase()
      // .replace(/[ĂÂÎȘȚăâîșț]/g, match => romanianMap[match])
      .replace(/\s+/g, '-')
    // .replace(/[^\w-]/g, '');
  }

  toggleTags() {
    this.tagsOpen = !this.tagsOpen;
    if (this.tagComponent) {
      // Call updateHeight to make sure the height is updated
      this.tagComponent.updateHeight();
    }
  }

  updateSelectedFilters(filters: { category: string, value: string, label: string }[]): void {
    this.selectedFilters = filters;
  }

  removeFilter(category: string, value: string): void {
    // Remove the filter from the selected filters
    this.selectedFilters = this.selectedFilters.filter(filter => !(filter.category === category && filter.value === value));

    // Call the child component's removeFilter method to keep the state in sync
    if (this.tagComponent) {
      this.tagComponent.removeFilter(category, value);

      // After removing the filter, create the selected tags object
      const selectedTags = this.tagComponent.createSelectedTagsObject();

      // Call attachTags to update the state
      this.tagComponent.attachTags(selectedTags.tags);
    }
  }

  openIcpScoreModal() {
    // Emit event when user clicks to open ICP Score modal
    this.icpScoreClicked.emit();
  }

  onEditPromptToggled(eventData: { visible: boolean, assistantPrompt: string, promptValue: InnerHTML }) {
    this.editPromptToggled.emit(eventData);  // Emit to parent
  }

}
